import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Globe, Palette, KeyRound, Layers, Shield, ArrowLeft,
  Plus, Copy, Check, Smartphone,
} from '@/lib/icons';
import { platformApi } from '@/features/platform/api';
import { useNavigate } from '@tanstack/react-router';
import { useToast } from '@/hooks/use-toast';
import {
  Card, CardContent,
} from '@/components/ui/card';
import { BrandingTab } from '@/features/platform/branding-tab';
import { AppVersionsTab } from '@/features/platform/app-versions-tab';
import { isSuperAdmin } from '@/lib/auth/roles';
import { useAppSelector } from '@/store/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatDate } from '@/lib/dates';

export const Route = createFileRoute(
  '/_layout/settings/platform/tenants/$tenantId'
)({
  component: PlatformTenantDetailPage,
});

/* ─── Data fetchers ──────────────────────────────────────────────────── */

/**
 * Normalize any list endpoint response to a plain array.
 *
 * Platform-service is inconsistent across resources: domains/entitlements/
 * credentials return `OperationResult<list>` (array at `data.value`), while
 * api-keys returns a bare paginated page (`{ items, total, limit, offset }`).
 * Unwrapping only `data.value ?? data` left api-keys as an object, so the
 * table's `.map` threw and tripped the error boundary. This collapses every
 * shape (OperationResult-wrapped page, bare page, OperationResult array, bare
 * array) and is guaranteed to return an array — so a list tab can never crash
 * on a `.map`, regardless of which envelope the backend uses.
 */
function toList<T = unknown>(resp: { data?: unknown }): T[] {
  const data = resp?.data as Record<string, unknown> | undefined;
  const value = data?.value as Record<string, unknown> | undefined;
  const candidates: unknown[] = [value?.items, data?.items, data?.value, data];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as T[];
  }
  return [];
}

async function fetchTenant(tenantId: string) {
  const resp = await platformApi.get(`/api/v1/tenants/${tenantId}`);
  return resp.data?.value ?? resp.data;
}

async function fetchDomains(tenantId: string) {
  try {
    return toList(await platformApi.get(`/api/v1/tenants/${tenantId}/domains`));
  } catch { return []; }
}

async function fetchEntitlements(tenantId: string) {
  try {
    return toList(await platformApi.get(`/api/v1/tenants/${tenantId}/entitlements`));
  } catch { return []; }
}

async function fetchApiKeys(tenantId: string) {
  try {
    return toList(await platformApi.get(`/api/v1/tenants/${tenantId}/api-keys`));
  } catch { return []; }
}

async function fetchCredentials(tenantId: string) {
  try {
    return toList(await platformApi.get(`/api/v1/tenants/${tenantId}/credentials`));
  } catch { return []; }
}

/* ─── Domains Tab ────────────────────────────────────────────────────── */

function DomainsTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: domains = [], isLoading } = useQuery({
    queryKey: ['platform-domains', tenantId],
    queryFn: () => fetchDomains(tenantId),
  });

  const [newHost, setNewHost] = useState('');
  const [domainType, setDomainType] = useState('subdomain');
  const [addOpen, setAddOpen] = useState(false);

  const addMutation = useMutation({
    mutationFn: async () => {
      const resp = await platformApi.post(`/api/v1/tenants/${tenantId}/domains`, {
        host: newHost.toLowerCase(), domainType, isPrimary: false,
      });
      return resp.data?.value ?? resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-domains', tenantId] });
      setAddOpen(false);
      setNewHost('');
      toast({ title: 'Domain added' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add domain.', variant: 'destructive' });
    },
  });

  if (isLoading) return <LoadingSkeleton rows={3} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{domains.length} domain{domains.length !== 1 ? 's' : ''} registered</p>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1.5" />Add Domain</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Domain</DialogTitle>
              <DialogDescription>Register a hostname for tenant resolution. Must be lowercased.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 my-4">
              <div>
                <label className="text-sm font-medium">Hostname</label>
                <Input value={newHost} onChange={(e) => setNewHost(e.target.value)} placeholder="xyz.mazeedpay.com" className="mt-1.5 font-mono" />
              </div>
              <div>
                <label className="text-sm font-medium">Type</label>
                <Select value={domainType} onValueChange={setDomainType}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="subdomain">Subdomain</SelectItem>
                    <SelectItem value="custom">Custom Domain</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button disabled={!newHost.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
                {addMutation.isPending ? 'Adding...' : 'Add Domain'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {domains.length === 0 ? (
        <EmptyState icon={<Globe className="h-8 w-8" />} message="No domains configured" />
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Hostname</TableHead><TableHead>Type</TableHead><TableHead>Primary</TableHead><TableHead>Verified</TableHead><TableHead>SSL</TableHead></TableRow></TableHeader>
          <TableBody>
            {domains.map((d: any) => (
              <TableRow key={d.id ?? d.host}>
                <TableCell className="font-mono text-sm">{d.host}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{d.domainType}</Badge></TableCell>
                <TableCell>{d.isPrimary && <Check className="h-4 w-4 text-emerald-500" />}</TableCell>
                <TableCell>{d.verifiedAt ? <Check className="h-4 w-4 text-emerald-500" /> : <Badge variant="outline" className="text-xs text-amber-600">pending</Badge>}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{d.sslStatus ?? 'pending'}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/* ─── Entitlements Tab ───────────────────────────────────────────────── */

function EntitlementsTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: entitlements = [], isLoading } = useQuery({
    queryKey: ['platform-entitlements', tenantId],
    queryFn: () => fetchEntitlements(tenantId),
  });

  const [addOpen, setAddOpen] = useState(false);
  const [productCode, setProductCode] = useState('');

  const addMutation = useMutation({
    mutationFn: async () => {
      const resp = await platformApi.post(`/api/v1/tenants/${tenantId}/entitlements`, {
        productCode, status: 'active',
      });
      return resp.data?.value ?? resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-entitlements', tenantId] });
      setAddOpen(false);
      setProductCode('');
      toast({ title: 'Entitlement added' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add entitlement.', variant: 'destructive' });
    },
  });

  if (isLoading) return <LoadingSkeleton rows={2} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{entitlements.length} product{entitlements.length !== 1 ? 's' : ''} entitled</p>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1.5" />Entitle Product</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Entitle Product</DialogTitle>
              <DialogDescription>Grant this tenant access to a platform product.</DialogDescription>
            </DialogHeader>
            <div className="my-4">
              <label className="text-sm font-medium">Product Code</label>
              <Input value={productCode} onChange={(e) => setProductCode(e.target.value)} placeholder="e.g. device_financing" className="mt-1.5 font-mono" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button disabled={!productCode.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
                {addMutation.isPending ? 'Entitling...' : 'Entitle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {entitlements.length === 0 ? (
        <EmptyState icon={<Layers className="h-8 w-8" />} message="No products entitled" />
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Product Code</TableHead><TableHead>Status</TableHead><TableHead>Config</TableHead></TableRow></TableHeader>
          <TableBody>
            {entitlements.map((e: any) => (
              <TableRow key={e.id ?? e.productCode}>
                <TableCell className="font-mono text-sm font-medium">{e.productCode}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={e.status === 'active' ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' : 'bg-red-500/15 text-red-700 border-red-500/30'}>
                    {e.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono">
                  {e.config ? JSON.stringify(e.config).slice(0, 50) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/* ─── API Keys Tab ───────────────────────────────────────────────────── */

function ApiKeysTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: keys = [], isLoading } = useQuery({
    queryKey: ['platform-api-keys', tenantId],
    queryFn: () => fetchApiKeys(tenantId),
  });

  const [mintOpen, setMintOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [keyRole, setKeyRole] = useState('TenantAdmin');
  const [mintedKey, setMintedKey] = useState<string | null>(null);

  const mintMutation = useMutation({
    mutationFn: async () => {
      // Backend requires at least one role; scopes defaults to all services.
      const resp = await platformApi.post(`/api/v1/tenants/${tenantId}/api-keys`, {
        name: keyName.trim(),
        roles: [keyRole],
        scopes: ['*'],
      });
      return resp.data?.value ?? resp.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['platform-api-keys', tenantId] });
      setMintedKey(data.rawKey ?? data.key ?? null);
      setKeyName('');
      toast({ title: 'API key minted' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to mint API key.', variant: 'destructive' });
    },
  });

  const copyKey = async () => {
    if (mintedKey) {
      await navigator.clipboard.writeText(mintedKey);
      toast({ title: 'Copied', description: 'API key copied to clipboard.' });
    }
  };

  if (isLoading) return <LoadingSkeleton rows={2} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{keys.length} key{keys.length !== 1 ? 's' : ''}</p>
        <Dialog open={mintOpen} onOpenChange={(open) => { setMintOpen(open); if (!open) setMintedKey(null); }}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1.5" />Mint Key</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{mintedKey ? 'Key Created — Copy Now' : 'Mint API Key'}</DialogTitle>
              <DialogDescription>
                {mintedKey
                  ? 'This is the only time you can see the full key. Copy it now.'
                  : 'Create a machine-to-machine API key for this tenant. prefix: "mpay_"'}
              </DialogDescription>
            </DialogHeader>
            {mintedKey ? (
              <div className="space-y-3 py-4">
                <div className="flex items-center gap-2">
                  <Input value={mintedKey} readOnly className="font-mono text-xs" />
                  <Button size="icon" variant="outline" onClick={copyKey}><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm font-medium">Key Name</label>
                  <Input value={keyName} onChange={(e) => setKeyName(e.target.value)} placeholder="e.g. Production Gateway" className="mt-1.5" />
                </div>
                <div>
                  <label className="text-sm font-medium">Role</label>
                  <Select value={keyRole} onValueChange={setKeyRole}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TenantAdmin">Tenant Admin</SelectItem>
                      <SelectItem value="User">User</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Each consuming service maps this role to its own permissions. Scope covers all services (<span className="font-mono">*</span>).
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              {mintedKey ? (
                <Button onClick={() => { setMintOpen(false); setMintedKey(null); }}>Done</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setMintOpen(false)}>Cancel</Button>
                  <Button disabled={!keyName.trim() || mintMutation.isPending} onClick={() => mintMutation.mutate()}>
                    {mintMutation.isPending ? 'Minting...' : 'Mint Key'}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {keys.length === 0 ? (
        <EmptyState icon={<KeyRound className="h-8 w-8" />} message="No API keys" />
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Prefix</TableHead><TableHead>Scopes</TableHead><TableHead>Status</TableHead><TableHead>Last Used</TableHead></TableRow></TableHeader>
          <TableBody>
            {keys.map((k: any) => (
              <TableRow key={k.id ?? k.keyPrefix}>
                <TableCell className="font-medium">{k.name}</TableCell>
                <TableCell className="font-mono text-xs">{k.keyPrefix}…</TableCell>
                <TableCell className="text-xs text-muted-foreground font-mono">{JSON.stringify(k.scopes ?? ['*']).slice(0, 30)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={k.revokedAt ? 'bg-red-500/15 text-red-700 border-red-500/30' : 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30'}>
                    {k.revokedAt ? 'revoked' : 'active'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {k.lastUsedAt ? formatDate(k.lastUsedAt) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/* ─── Provider Credentials Tab ───────────────────────────────────────── */

function CredentialsTab({ tenantId }: { tenantId: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: creds = [], isLoading } = useQuery({
    queryKey: ['platform-credentials', tenantId],
    queryFn: () => fetchCredentials(tenantId),
  });

  const [addOpen, setAddOpen] = useState(false);
  const [providerType, setProviderType] = useState('payment');
  const [providerName, setProviderName] = useState('');
  const [credentials, setCredentials] = useState('');

  const addMutation = useMutation({
    mutationFn: async () => {
      const resp = await platformApi.post(`/api/v1/tenants/${tenantId}/credentials`, {
        providerType,
        providerName,
        credentials: JSON.parse(credentials),
      });
      return resp.data?.value ?? resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-credentials', tenantId] });
      setAddOpen(false);
      setProviderName('');
      setCredentials('');
      toast({ title: 'Credential stored', description: 'Provider credentials encrypted and saved.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to store credentials. Check JSON format.', variant: 'destructive' });
    },
  });

  if (isLoading) return <LoadingSkeleton rows={2} />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{creds.length} credential{creds.length !== 1 ? 's' : ''} stored · AES-256-GCM encrypted</p>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline"><Plus className="h-4 w-4 mr-1.5" />Add Credential</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Add Provider Credential</DialogTitle>
              <DialogDescription>Store encrypted provider credentials for this tenant. Credentials are AES-256-GCM encrypted at rest.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 my-4">
              <div>
                <label className="text-sm font-medium">Provider Type</label>
                <Select value={providerType} onValueChange={setProviderType}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Provider Name</label>
                <Input value={providerName} onChange={(e) => setProviderName(e.target.value)} placeholder="e.g. Hormuud ZAAD" className="mt-1.5" />
              </div>
              <div>
                <label className="text-sm font-medium">Credentials JSON</label>
                <textarea
                  value={credentials}
                  onChange={(e) => setCredentials(e.target.value)}
                  placeholder={'{\n  "apiKey": "...",\n  "secret": "..."\n}'}
                  className="mt-1.5 w-full h-28 rounded-md border bg-transparent px-3 py-2 text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button disabled={!providerName.trim() || !credentials.trim() || addMutation.isPending} onClick={() => addMutation.mutate()}>
                {addMutation.isPending ? 'Encrypting...' : 'Store Credential'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {creds.length === 0 ? (
        <EmptyState icon={<Shield className="h-8 w-8" />} message="No provider credentials stored" />
      ) : (
        <Table>
          <TableHeader><TableRow><TableHead>Provider</TableHead><TableHead>Type</TableHead><TableHead>Stage</TableHead><TableHead>Active</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
          <TableBody>
            {creds.map((c: any) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">{c.providerName}</TableCell>
                <TableCell><Badge variant="outline" className="text-xs">{c.providerType}</Badge></TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    c.stage === 'active' ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30' :
                    c.stage === 'staged' ? 'bg-amber-500/15 text-amber-700 border-amber-500/30' :
                    'bg-red-500/15 text-red-700 border-red-500/30'
                  }>
                    {c.stage}
                  </Badge>
                </TableCell>
                <TableCell>
                  {c.isActive ? <Check className="h-4 w-4 text-emerald-500" /> : <Badge variant="outline" className="text-xs text-red-600">inactive</Badge>}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {formatDate(c.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

/* ─── Shared Components ──────────────────────────────────────────────── */

function LoadingSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-3 p-4">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

function EmptyState({ icon, message }: { icon: React.ReactNode; message: string }) {
  return (
    <div className="text-center py-12 text-muted-foreground text-sm">
      <div className="mx-auto mb-2 opacity-40 w-fit">{icon}</div>
      {message}
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────────────────────── */

function PlatformTenantDetailPage() {
  const { tenantId } = Route.useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canManageAppVersions = isSuperAdmin(useAppSelector((state) => state.auth.user));

  const { data: tenant, isLoading } = useQuery({
    queryKey: ['platform-tenant', tenantId],
    queryFn: () => fetchTenant(tenantId),
  });

  if (isLoading) {
    return (
      <div className="space-y-4 animate-fade-in">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <p>Tenant not found</p>
        <Button variant="link" onClick={() => navigate({ to: '/settings/platform/tenants' })}>
          Back to Platform Tenants
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: '/settings/platform/tenants' })}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">{tenant.name}</h1>
          <p className="text-xs text-muted-foreground font-mono mt-0.5">{tenant.id}</p>
        </div>
        <Badge
          variant="outline"
          className={
            tenant.status === 'active'
              ? 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30'
              : 'bg-amber-500/15 text-amber-700 border-amber-500/30'
          }
        >
          {tenant.status}
        </Badge>
        {tenant.houseBrandFallback && (
          <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30">
            House Brand
          </Badge>
        )}
      </div>

      {/* Tabbed content */}
      <Tabs defaultValue="branding">
        <TabsList className={`grid ${canManageAppVersions ? 'grid-cols-6' : 'grid-cols-5'} w-full max-w-2xl`}>
          <TabsTrigger value="branding" className="flex items-center gap-1.5 text-xs">
            <Palette className="h-3.5 w-3.5" />Branding
          </TabsTrigger>
          <TabsTrigger value="domains" className="flex items-center gap-1.5 text-xs">
            <Globe className="h-3.5 w-3.5" />Domains
          </TabsTrigger>
          <TabsTrigger value="entitlements" className="flex items-center gap-1.5 text-xs">
            <Layers className="h-3.5 w-3.5" />Products
          </TabsTrigger>
          <TabsTrigger value="api-keys" className="flex items-center gap-1.5 text-xs">
            <KeyRound className="h-3.5 w-3.5" />API Keys
          </TabsTrigger>
          <TabsTrigger value="credentials" className="flex items-center gap-1.5 text-xs">
            <Shield className="h-3.5 w-3.5" />Credentials
          </TabsTrigger>
          {canManageAppVersions && (
            <TabsTrigger value="app-versions" className="flex items-center gap-1.5 text-xs">
              <Smartphone className="h-3.5 w-3.5" />{t('settings.platform.appVersions.tab')}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="branding" className="mt-6">
          <Card><CardContent className="pt-6"><BrandingTab tenantId={tenantId} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="domains" className="mt-6">
          <Card><CardContent className="pt-6"><DomainsTab tenantId={tenantId} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="entitlements" className="mt-6">
          <Card><CardContent className="pt-6"><EntitlementsTab tenantId={tenantId} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="api-keys" className="mt-6">
          <Card><CardContent className="pt-6"><ApiKeysTab tenantId={tenantId} /></CardContent></Card>
        </TabsContent>
        <TabsContent value="credentials" className="mt-6">
          <Card><CardContent className="pt-6"><CredentialsTab tenantId={tenantId} /></CardContent></Card>
        </TabsContent>
        {canManageAppVersions && (
          <TabsContent value="app-versions" className="mt-6">
            <Card><CardContent className="pt-6"><AppVersionsTab tenantId={tenantId} /></CardContent></Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
